import {concat, difference} from 'utilities/Set';
import {mod} from 'utilities/number';
import {createSelector} from 'reselect';
import memoize from 'lodash/memoize';

export const selectCardById = (state, id) => state.card.entities[id];

const reselectUncollectedCardIdsByStackId = createSelector(
  state => state.stack.stack.collectionsByStackId,
  state => state.card.byCollectionId,
  state => state.card.byStackId,
  (collectionsByStackId, cardsByCollectionId, cardsByStackId) =>
    memoize(stackId => {
      const collectionIds = collectionsByStackId[stackId] || [];
      const collectedCardIds = Array.from(collectionIds).reduce(
        (memo, collectionId) => concat(memo, cardsByCollectionId[collectionId]),
        new Set()
      );
      const cardIds = cardsByStackId[stackId];
      return Array.from(difference(cardIds, collectedCardIds));
    })
);

export const selectUncollectedCardIdsByStackId = (state, stackId) =>
  reselectUncollectedCardIdsByStackId(state)(stackId);

const reselectCardIdsByCollectionId = createSelector(
  state => state.card.byCollectionId,
  byCollectionId =>
    memoize(collectionId => Array.from(byCollectionId[collectionId] || []))
);

export const selectCardIdsByCollectionId = (state, collectionId) =>
  reselectCardIdsByCollectionId(state)(collectionId);

const reselectCardIdsByStackId = createSelector(
  state => state.card.byStackId,
  byStackId => memoize(stackId => Array.from(byStackId[stackId] || []))
);

export const selectCardIdsByStackId = (state, stackId) =>
  reselectCardIdsByStackId(state)(stackId);

const selectSiblingCardId = (state, stackId, cardId, delta) => {
  const cardIds = selectCardIdsByStackId(state, stackId);
  const length = cardIds.length;
  const index = cardIds.indexOf(cardId);
  if (length < 3 || index === -1) return null;
  return cardIds[mod(index + delta, length)];
};

export const selectNextCardId = (state, stackId, cardId) =>
  selectSiblingCardId(state, stackId, cardId, 1);

export const selectPrevCardId = (state, stackId, cardId) =>
  selectSiblingCardId(state, stackId, cardId, -1);

export const selectStatisticsByCardId = (state, cardId) =>
  state.card.statisticsByCardId[cardId];

export const selectIsCardAccessDenied = (state, cardId) =>
  state.card.withoutAccess.has(cardId);

export const selectHasSampleData = (state, cardId) => {
  const card = selectCardById(state, cardId);

  return !!(
    card &&
    card.datasources &&
    card.datasources.some(s => s.isSampleData)
  );
};

export const selectNotebookCard = (state, cardId) =>
  state.card.notebooks[cardId];

export const selectIsDomoApp = (state, cardId) => {
  const card = selectCardById(state, cardId);
  return card && card.type === 'domoapp';
};
